import { useState, useEffect } from 'react';
import { SubscriptionData } from '@/models/User';

interface User {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user';
  subscription?: SubscriptionData;
  twofa_enabled: boolean;
  is_suspended: boolean;
  created_at: string;
}

export function useRole() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        // Only access localStorage on the client side
        if (typeof window !== 'undefined') {
          const storedUser = localStorage.getItem('user');
          const token = localStorage.getItem('access_token');
          if (storedUser && token) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
          }
        }
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);
  

  const isAdmin = () => user?.role === 'admin';
  const isUser = () => user?.role === 'user' || user?.role === 'admin';
  const hasRole = (requiredRole: 'admin' | 'user') => {
    if (requiredRole === 'admin') {
      return user?.role === 'admin';
    }
    return user?.role === 'user' || user?.role === 'admin';
  };

  const updateUserRole = (newRole: 'admin' | 'user') => {
    if (user && typeof window !== 'undefined') {
      const updatedUser = { ...user, role: newRole };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const refreshUser = () => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error refreshing user from localStorage:', error);
        }
      }
    }
  };

  return {
    user,
    loading,
    isAdmin,
    isUser,
    hasRole,
    updateUserRole,
    refreshUser
  };
}
